"use client"

/**
 * Public AgentHub roadmap.
 *
 * Design rules applied here (dark-theme contrast tokens, WCAG AA):
 *   - Page bg: zinc-950
 *   - Card surface: SOLID zinc-900 (no opacity)
 *   - Elevated: zinc-800 (spotlight, hero progress)
 *   - Borders: zinc-800 subtle / zinc-700 clear
 *   - Text: zinc-50 (heading), zinc-200 (body), zinc-400 (label),
 *     zinc-500 (caption only). Never text-foreground/opacity — that's
 *     what gave us "gray on gray" in the previous attempt.
 *   - Status colour shows up in EXACTLY two places: the left-edge bar
 *     of an item card, and a single small dot inside the StatusPill.
 *     Pills themselves sit on solid zinc-800 with zinc-200 text so
 *     they're readable on every surface.
 */

import { useEffect, useMemo, useState, createContext, useContext } from "react"
import {
  ROADMAP_ITEMS,
  AREA_META,
  STATUS_META,
  eightyTwentyScore,
  topPicks,
  statusCounts,
  mvpProgress,
  type RoadmapArea,
  type RoadmapStatus,
  type RoadmapEffort,
  type RoadmapItem,
} from "./data"

type ScopeFilter = "all" | "mvp" | "post-mvp"

// ─── MVP overrides — localStorage-backed per-browser persistence ────────
//
// Public roadmap is read-only on the server. Owner може у себе на компі
// перемикати MVP/post-MVP стан кожного пункту і це збережеться у
// localStorage. Інші відвідувачі бачать дефолтний `mvp` у data.ts.

const LS_KEY = "agenthub_roadmap_mvp_overrides_v1"
type MvpOverrides = Record<string, boolean>

const OverridesCtx = createContext<{
  overrides: MvpOverrides
  toggle: (id: string, defaultMvp: boolean) => void
  isMvp: (item: RoadmapItem) => boolean
}>({ overrides: {}, toggle: () => {}, isMvp: () => false })

function useOverridesProvider() {
  const [overrides, setOverrides] = useState<MvpOverrides>({})
  const [hydrated, setHydrated] = useState(false)

  // Load once on mount.
  useEffect(() => {
    try {
      const raw = typeof window !== "undefined" ? localStorage.getItem(LS_KEY) : null
      if (raw) setOverrides(JSON.parse(raw) as MvpOverrides)
    } catch {
      /* ignore */
    }
    setHydrated(true)
  }, [])

  // Persist on every change.
  useEffect(() => {
    if (!hydrated) return
    try {
      localStorage.setItem(LS_KEY, JSON.stringify(overrides))
    } catch {
      /* ignore quota / private mode */
    }
  }, [overrides, hydrated])

  const toggle = (id: string, defaultMvp: boolean) => {
    setOverrides((prev) => {
      const current = id in prev ? prev[id] : defaultMvp
      const next = !current
      // If toggle returns to default → drop the key, keep state minimal.
      if (next === defaultMvp) {
        const { [id]: _, ...rest } = prev
        void _
        return rest
      }
      return { ...prev, [id]: next }
    })
  }

  const isMvp = (item: RoadmapItem) =>
    item.id in overrides ? overrides[item.id] : !!item.mvp

  return { overrides, toggle, isMvp }
}

// ─── Status display ────────────────────────────────────────────────────────

const STATUS_DOT: Record<RoadmapStatus, string> = {
  done: "bg-emerald-400",
  partial: "bg-amber-400",
  planned: "bg-sky-400",
  backlog: "bg-zinc-500",
}

const STATUS_LEFT_BAR: Record<RoadmapStatus, string> = {
  done: "border-l-emerald-500",
  partial: "border-l-amber-500",
  planned: "border-l-sky-500",
  backlog: "border-l-zinc-600",
}

const STATUS_TEXT_ACCENT: Record<RoadmapStatus, string> = {
  done: "text-emerald-300",
  partial: "text-amber-300",
  planned: "text-sky-300",
  backlog: "text-zinc-400",
}

// ─── Effort display ────────────────────────────────────────────────────────

const EFFORT_DURATION: Record<RoadmapEffort, string> = {
  S: "2-4 год",
  M: "1-2 дні",
  L: "3-7 днів",
  XL: "2+ тижні",
}

// ─── Pills (solid bg, high-contrast text) ──────────────────────────────────

function StatusPill({ status }: { status: RoadmapStatus }) {
  return (
    <span className="inline-flex items-center gap-1.5 rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10.5px] font-medium text-zinc-200">
      <span className={`h-1.5 w-1.5 rounded-full ${STATUS_DOT[status]}`} />
      <span className={STATUS_TEXT_ACCENT[status]}>
        {STATUS_META[status].label}
      </span>
    </span>
  )
}

function EffortPill({ effort }: { effort: RoadmapEffort }) {
  return (
    <span
      title={`Effort ${effort}`}
      className="inline-flex items-center gap-1 rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10.5px] text-zinc-300"
    >
      <span className="font-bold text-zinc-100">{effort}</span>
      <span className="text-zinc-400">·</span>
      <span>{EFFORT_DURATION[effort]}</span>
    </span>
  )
}

function ImpactBadge({ value }: { value: 1 | 2 | 3 | 4 | 5 }) {
  return (
    <span
      title={`Impact ${value}/5`}
      className="inline-flex items-center rounded-full border border-zinc-700 bg-zinc-800 px-2 py-0.5 text-[10px] tracking-tight"
    >
      <span className="text-amber-300">{"★".repeat(value)}</span>
      <span className="text-zinc-600">{"★".repeat(5 - value)}</span>
    </span>
  )
}

// ─── Page ──────────────────────────────────────────────────────────────────

export default function RoadmapPage() {
  const overridesValue = useOverridesProvider()
  const { isMvp } = overridesValue

  const [areaFilter, setAreaFilter] = useState<"all" | RoadmapArea>("all")
  const [statusFilter, setStatusFilter] = useState<"all" | RoadmapStatus>("all")
  const [scopeFilter, setScopeFilter] = useState<ScopeFilter>("all")

  const filtered = useMemo(() => {
    return ROADMAP_ITEMS.filter((i) => {
      if (areaFilter !== "all" && i.area !== areaFilter) return false
      if (statusFilter !== "all" && i.status !== statusFilter) return false
      const inMvp = isMvp(i)
      if (scopeFilter === "mvp" && !inMvp) return false
      if (scopeFilter === "post-mvp" && inMvp) return false
      return true
    })
  }, [areaFilter, statusFilter, scopeFilter, isMvp])

  const counts = statusCounts(ROADMAP_ITEMS)
  const total = ROADMAP_ITEMS.length
  const donePct = Math.round((counts.done / total) * 100)

  // MVP-progress with overrides applied — re-derive each render so the
  // bar updates instantly when user flips a toggle.
  const mvp = useMemo(() => {
    const items = ROADMAP_ITEMS.map((i) => ({ ...i, mvp: isMvp(i) }))
    return mvpProgress(items)
  }, [isMvp])

  const top8020 = topPicks(ROADMAP_ITEMS, 6)

  const grouped = useMemo(() => {
    const map = new Map<RoadmapArea, RoadmapItem[]>()
    for (const i of filtered) {
      const arr = map.get(i.area) ?? []
      arr.push(i)
      map.set(i.area, arr)
    }
    const statusOrder: Record<RoadmapStatus, number> = {
      partial: 0,
      planned: 1,
      backlog: 2,
      done: 3,
    }
    for (const [, arr] of map) {
      arr.sort((a, b) => {
        if (a.status !== b.status) return statusOrder[a.status] - statusOrder[b.status]
        return eightyTwentyScore(b) - eightyTwentyScore(a)
      })
    }
    return Array.from(map.entries())
  }, [filtered])

  return (
    <OverridesCtx.Provider value={overridesValue}>
    <div className="min-h-screen bg-zinc-950 text-zinc-200">
      <div className="mx-auto max-w-7xl px-4 py-8 sm:px-6 lg:px-10 lg:py-12">
        {/* Hero */}
        <header className="mb-10 flex flex-col gap-6 sm:flex-row sm:items-end sm:justify-between">
          <div>
            <div className="text-[11px] font-semibold uppercase tracking-[0.2em] text-zinc-400">
              AgentHub Roadmap
            </div>
            <h1 className="mt-1 text-3xl font-bold tracking-tight text-zinc-50 sm:text-4xl">
              Що вже є · що йде далі
            </h1>
            <p className="mt-3 max-w-2xl text-sm text-zinc-300">
              Усі модулі платформи з їх статусом, складністю і впливом.
              Сортовано за правилом 80/20 — найбільший impact за найменший
              effort нагору.
            </p>
          </div>

          {/* Progress card — двосмугова шкала: загальна + до MVP */}
          <div className="rounded-2xl border border-zinc-700 bg-zinc-900 px-5 py-4 sm:min-w-[320px]">
            <div className="text-[10px] font-semibold uppercase tracking-wider text-zinc-400">
              Загальний прогрес
            </div>
            <div className="mt-1 flex items-baseline gap-2">
              <span className="text-3xl font-bold tabular-nums text-zinc-50">
                {donePct}%
              </span>
              <span className="text-[12px] text-zinc-400">
                {counts.done} з {total} готово
              </span>
            </div>
            <div className="mt-2 h-2 overflow-hidden rounded-full bg-zinc-800">
              <div
                className="h-full rounded-full bg-emerald-500 transition-all"
                style={{ width: `${donePct}%` }}
              />
            </div>
            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px] text-zinc-300">
              <ProgressDot color="bg-emerald-400" label={`${counts.done} готово`} />
              <ProgressDot color="bg-amber-400" label={`${counts.partial} частково`} />
              <ProgressDot color="bg-sky-400" label={`${counts.planned} план`} />
              <ProgressDot color="bg-zinc-500" label={`${counts.backlog} бэклог`} />
            </div>

            {/* MVP-row */}
            <div className="mt-4 border-t border-zinc-800 pt-3">
              <div className="flex items-baseline justify-between gap-2">
                <div className="flex items-center gap-1.5">
                  <span className="text-base">🚀</span>
                  <span className="text-[10px] font-semibold uppercase tracking-wider text-violet-300">
                    До запуску MVP
                  </span>
                </div>
                <span className="text-lg font-bold tabular-nums text-zinc-50">
                  {mvp.pct}%
                </span>
              </div>
              <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-zinc-800">
                <div
                  className="h-full rounded-full bg-violet-500 transition-all"
                  style={{ width: `${mvp.pct}%` }}
                />
              </div>
              <div className="mt-1.5 text-[10.5px] text-zinc-400">
                {mvp.done} готово · {mvp.partial} частково · {mvp.remaining} лишилось ·
                всього {mvp.total} в MVP-scope
              </div>
            </div>
          </div>
        </header>

        {/* 80/20 spotlight — elevated surface, violet only on title accent */}
        <section className="mb-10 rounded-2xl border border-zinc-800 bg-zinc-900 p-5 sm:p-6">
          <div className="mb-4 flex flex-wrap items-baseline gap-x-3 gap-y-1">
            <h2 className="flex items-center gap-2 text-base font-bold text-zinc-50">
              <span className="text-violet-400">🎯</span>
              <span>80/20 — куди йти зараз</span>
            </h2>
            <span className="text-[12px] text-zinc-400">
              максимальний impact ÷ effort · топ-{top8020.length}
            </span>
          </div>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {top8020.map((it, idx) => (
              <SpotlightCard key={it.id} rank={idx + 1} item={it} />
            ))}
          </div>
        </section>

        {/* Scope filter strip — MVP / Post-MVP */}
        <div className="mb-3 flex flex-wrap items-center gap-2">
          <span className="text-[10.5px] font-semibold uppercase tracking-wider text-zinc-500">
            Scope:
          </span>
          <FilterChip
            label="🌐 Усе"
            active={scopeFilter === "all"}
            onClick={() => setScopeFilter("all")}
          />
          <FilterChip
            label={`🚀 MVP · ${mvp.pct}%`}
            active={scopeFilter === "mvp"}
            onClick={() => setScopeFilter("mvp")}
          />
          <FilterChip
            label="🌟 Post-MVP"
            active={scopeFilter === "post-mvp"}
            onClick={() => setScopeFilter("post-mvp")}
          />
        </div>

        {/* Filter strip */}
        <div className="mb-6 flex flex-wrap items-center gap-2">
          <FilterChip
            label="Усі"
            active={areaFilter === "all"}
            onClick={() => setAreaFilter("all")}
          />
          {Object.entries(AREA_META).map(([key, meta]) => (
            <FilterChip
              key={key}
              label={`${meta.emoji} ${meta.label}`}
              active={areaFilter === key}
              onClick={() => setAreaFilter(key as RoadmapArea)}
            />
          ))}
          <span className="mx-2 hidden h-5 w-px bg-zinc-800 sm:block" />
          <FilterChip
            label="Усі статуси"
            active={statusFilter === "all"}
            onClick={() => setStatusFilter("all")}
          />
          {Object.entries(STATUS_META).map(([key, meta]) => (
            <FilterChip
              key={key}
              label={`${meta.emoji} ${meta.label}`}
              active={statusFilter === key}
              onClick={() => setStatusFilter(key as RoadmapStatus)}
            />
          ))}
        </div>

        {/* Grouped grid */}
        {grouped.length === 0 ? (
          <div className="rounded-xl border border-zinc-800 bg-zinc-900 px-4 py-10 text-center text-sm text-zinc-400">
            За поточними фільтрами нічого. Спробуй прибрати один з фільтрів.
          </div>
        ) : (
          <div className="space-y-8">
            {grouped.map(([area, items]) => {
              const meta = AREA_META[area]
              const subCounts = items.reduce(
                (acc, i) => {
                  acc[i.status]++
                  return acc
                },
                { done: 0, partial: 0, planned: 0, backlog: 0 } as Record<
                  RoadmapStatus,
                  number
                >
              )
              return (
                <section key={area}>
                  <header className="mb-3 flex flex-wrap items-baseline gap-3 border-b border-zinc-800 pb-2">
                    <span className="text-lg">{meta.emoji}</span>
                    <h2 className="text-base font-semibold text-zinc-50">
                      {meta.label}
                    </h2>
                    <span className="text-[11.5px] text-zinc-400">
                      {meta.description}
                    </span>
                    <div className="ml-auto flex items-center gap-2.5 text-[11px] tabular-nums text-zinc-300">
                      {subCounts.done > 0 && (
                        <ProgressDot color="bg-emerald-400" label={`${subCounts.done}`} />
                      )}
                      {subCounts.partial > 0 && (
                        <ProgressDot color="bg-amber-400" label={`${subCounts.partial}`} />
                      )}
                      {subCounts.planned > 0 && (
                        <ProgressDot color="bg-sky-400" label={`${subCounts.planned}`} />
                      )}
                      {subCounts.backlog > 0 && (
                        <ProgressDot color="bg-zinc-500" label={`${subCounts.backlog}`} />
                      )}
                    </div>
                  </header>
                  <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
                    {items.map((i) => (
                      <ItemCard key={i.id} item={i} />
                    ))}
                  </div>
                </section>
              )
            })}
          </div>
        )}

        <footer className="mt-16 border-t border-zinc-800 pt-6 text-[11.5px] text-zinc-400">
          <p>
            Single source of truth:{" "}
            <code className="rounded bg-zinc-800 px-1 py-0.5 text-zinc-200">
              apps/web/app/roadmap/data.ts
            </code>
            . Спека партнера: Google Doc на 750+ KB; цей roadmap — її
            дистиляція в actionable items. AI Director може це читати — див.{" "}
            <code className="rounded bg-zinc-800 px-1 py-0.5 text-zinc-200">
              memory/agenthub_roadmap.md
            </code>
            .
          </p>
        </footer>
      </div>
    </div>
    </OverridesCtx.Provider>
  )
}

// ─── MVP toggle — segmented control [MVP | Post] ────────────────────────
//
// MVP зліва (фіолетовий, коли активний) — "до запуску".
// Post справа (зелений, коли активний) — "після запуску, ріст".
// Клік на сторону робить її активною. Однакова висота з StatusPill
// (px-2 py-0.5) щоб ряд pill-ів не "з'їжджав".

function MvpToggle({ item }: { item: RoadmapItem }) {
  const { isMvp, toggle } = useContext(OverridesCtx)
  const on = isMvp(item)
  const flip = (e: React.MouseEvent | React.KeyboardEvent) => {
    e.stopPropagation()
    toggle(item.id, !!item.mvp)
  }
  return (
    <span
      role="group"
      aria-label="MVP scope"
      className="inline-flex items-stretch overflow-hidden rounded-full border border-zinc-700 bg-zinc-800 text-[10px] font-semibold leading-none"
    >
      {/* MVP side — active = violet */}
      <button
        type="button"
        onClick={(e) => {
          if (on) e.stopPropagation()
          else flip(e)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            if (!on) flip(e)
          }
        }}
        title={on ? "Зараз у MVP" : "Перенести у MVP"}
        className={`px-2 py-0.5 transition-colors ${
          on
            ? "bg-violet-500 text-white"
            : "text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
        }`}
      >
        MVP
      </button>
      {/* Post-MVP side — active = emerald */}
      <button
        type="button"
        onClick={(e) => {
          if (!on) e.stopPropagation()
          else flip(e)
        }}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault()
            if (on) flip(e)
          }
        }}
        title={!on ? "Зараз у Post-MVP" : "Перенести у Post-MVP"}
        className={`border-l border-zinc-700 px-2 py-0.5 transition-colors ${
          !on
            ? "bg-emerald-500 text-white"
            : "text-zinc-400 hover:bg-zinc-700 hover:text-zinc-200"
        }`}
      >
        Post
      </button>
    </span>
  )
}

// ─── Sub-components ────────────────────────────────────────────────────────

function ProgressDot({ color, label }: { color: string; label: string }) {
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className={`h-1.5 w-1.5 rounded-full ${color}`} />
      <span>{label}</span>
    </span>
  )
}

function SpotlightCard({ rank, item }: { rank: number; item: RoadmapItem }) {
  const [open, setOpen] = useState(false)
  const meta = AREA_META[item.area]
  const score = eightyTwentyScore(item)
  // Картка розгортається якщо є будь-яка додаткова інформація — деталі
  // зі спеки, інженерна нотатка, або хоча б docRef.
  const hasDetails = !!item.details || !!item.note || !!item.docRef
  return (
    <div
      className={`relative flex h-full flex-col rounded-xl border border-zinc-700 border-l-[3px] ${STATUS_LEFT_BAR[item.status]} bg-zinc-800 p-3.5 transition-all ${
        hasDetails
          ? "cursor-pointer hover:-translate-y-0.5 hover:border-zinc-500"
          : ""
      }`}
      role={hasDetails ? "button" : undefined}
      tabIndex={hasDetails ? 0 : undefined}
      onClick={hasDetails ? () => setOpen((v) => !v) : undefined}
      onKeyDown={
        hasDetails
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                setOpen((v) => !v)
              }
            }
          : undefined
      }
    >
      <div className="mb-2 flex items-center justify-between gap-2">
        <div className="flex items-center gap-2">
          <span className="inline-flex h-5 min-w-[1.25rem] items-center justify-center rounded bg-violet-500/30 px-1.5 text-[10.5px] font-bold text-violet-100">
            #{rank}
          </span>
          <span className="text-[11px] font-medium text-zinc-400">
            {meta.emoji} {meta.label}
          </span>
        </div>
        <span className="font-mono text-[11px] font-semibold text-violet-300">
          ×{score.toFixed(1)}
        </span>
      </div>
      <h3 className="text-[14px] font-semibold leading-snug text-zinc-50">
        {item.title}
      </h3>
      <p className={`mt-1 text-[12px] leading-snug text-zinc-300 ${open ? "" : "line-clamp-2"}`}>
        {item.description}
      </p>
      {open && (
        <DetailsPanel docRef={item.docRef} details={item.details} note={item.note} />
      )}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-3">
        <StatusPill status={item.status} />
        <EffortPill effort={item.effort} />
        <ImpactBadge value={item.impact} />
        <MvpToggle item={item} />
        {hasDetails && (
          <span className="ml-auto text-[10.5px] text-zinc-500">
            {open ? "згорнути" : "деталі →"}
          </span>
        )}
      </div>
    </div>
  )
}

function ItemCard({ item }: { item: RoadmapItem }) {
  const [open, setOpen] = useState(false)
  const hasDetails = !!item.details || !!item.note || !!item.docRef
  return (
    <div
      className={`flex h-full flex-col rounded-xl border border-zinc-800 border-l-[3px] ${STATUS_LEFT_BAR[item.status]} bg-zinc-900 p-3.5 transition-all ${
        hasDetails
          ? "cursor-pointer hover:-translate-y-0.5 hover:border-zinc-700"
          : ""
      } ${item.status === "done" ? "opacity-75" : ""}`}
      role={hasDetails ? "button" : undefined}
      tabIndex={hasDetails ? 0 : undefined}
      onClick={hasDetails ? () => setOpen((v) => !v) : undefined}
      onKeyDown={
        hasDetails
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault()
                setOpen((v) => !v)
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-2">
        <h3 className="text-[13.5px] font-semibold leading-snug text-zinc-50">
          {item.title}
        </h3>
        <ImpactBadge value={item.impact} />
      </div>
      <p className="mt-1 text-[12px] leading-snug text-zinc-300">
        {item.description}
      </p>
      {item.note && !open && (
        <p className="mt-2 rounded border-l-2 border-zinc-700 bg-zinc-950 px-2 py-1 text-[11px] leading-snug text-zinc-400">
          {item.note}
        </p>
      )}
      {open && (
        <DetailsPanel docRef={item.docRef} details={item.details} note={item.note} />
      )}
      <div className="mt-auto flex flex-wrap items-center gap-1.5 pt-3">
        <StatusPill status={item.status} />
        <EffortPill effort={item.effort} />
        <MvpToggle item={item} />
        {item.doneIn && (
          <span className="ml-auto truncate font-mono text-[10px] text-zinc-500">
            {item.doneIn}
          </span>
        )}
        {hasDetails && !item.doneIn && (
          <span className="ml-auto text-[10.5px] text-zinc-500">
            {open ? "згорнути" : "деталі →"}
          </span>
        )}
      </div>
    </div>
  )
}

// ─── Details panel — opens inside a card on click ──────────────────────────
//
// Renders the spec excerpt + doc reference + (optionally) the engineering
// note. Markdown-lite: empty lines split paragraphs, "- " prefix turns into
// a bullet list, "**bold**" gets wrapped in <strong>.

function DetailsPanel({
  docRef,
  details,
  note,
}: {
  docRef?: string
  details?: string
  note?: string
}) {
  // Якщо геть нічого — нічого не рендеримо. Кнопку розгортання у такому
  // випадку взагалі не показуємо (hasDetails у батька виключає це).
  if (!docRef && !details && !note) return null
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-zinc-700 bg-zinc-950 p-3">
      {docRef && (
        <div className="flex flex-wrap items-baseline gap-1.5 text-[10.5px] font-medium uppercase tracking-wider text-zinc-400">
          <span>📖</span>
          <span>Посилання на спеку:</span>
          <span className="font-normal normal-case text-zinc-200">{docRef}</span>
        </div>
      )}
      {details ? (
        renderDetails(details)
      ) : !note ? (
        <p className="text-[12px] italic leading-snug text-zinc-500">
          Детальний опис цього пункту ще не витягнутий зі спеки.
          {docRef && " Дивись посилання вище для повного контексту."}
        </p>
      ) : null}
      {note && (
        <div className="rounded border-l-2 border-amber-500 bg-zinc-900 px-2.5 py-1.5 text-[11px] leading-snug text-zinc-300">
          <span className="font-semibold text-amber-300">Поточний стан: </span>
          {note}
        </div>
      )}
    </div>
  )
}

function renderDetails(details: string): React.ReactNode {
  const blocks = details.trim().split(/\n\n+/)
  return blocks.map((block, i) => {
    const lines = block.split("\n")
    // Bullet list block
    if (lines.every((l) => l.trim().startsWith("- "))) {
      return (
        <ul key={i} className="ml-4 list-disc space-y-1 text-[12px] leading-snug text-zinc-200">
          {lines.map((l, j) => (
            <li key={j}>{renderInline(l.replace(/^- /, "").trim())}</li>
          ))}
        </ul>
      )
    }
    // Paragraph
    return (
      <p key={i} className="text-[12px] leading-snug text-zinc-200">
        {renderInline(block)}
      </p>
    )
  })
}

function renderInline(text: string): React.ReactNode {
  // Split on **bold** segments. Even indices = plain, odd = bold.
  const parts = text.split(/\*\*([^*]+)\*\*/g)
  return parts.map((part, i) =>
    i % 2 === 1 ? (
      <strong key={i} className="font-semibold text-zinc-50">
        {part}
      </strong>
    ) : (
      <span key={i}>{part}</span>
    )
  )
}

function FilterChip({
  label,
  active,
  onClick,
}: {
  label: string
  active: boolean
  onClick: () => void
}) {
  const accent = active
    ? "border-zinc-500 bg-zinc-700 text-zinc-50"
    : "border-zinc-800 bg-zinc-900 text-zinc-300 hover:border-zinc-700 hover:text-zinc-100"
  return (
    <button
      type="button"
      onClick={onClick}
      className={`rounded-full border px-2.5 py-1 text-[11px] font-medium transition-colors ${accent}`}
    >
      {label}
    </button>
  )
}
